import { defineStore } from 'pinia'
import type { TourContent } from '@storyblok/tour'
import type { ISbRichtext } from 'storyblok-js-client'
import {
  DEMO_SPACE_THEME_CONTENT_URL,
  DEMO_SPACE_TOUR_HINTS_CONTENT_URL,
} from '@/constants/demo'
import type { JobRoleType } from '@/constants/jobRoles'
import { client } from '@/services/client'
import type { StoryContent, DemoSpaceTourContent } from '@/types'
import type { AssetContentRecord } from '@/types/content'
import type {
  QuickstartContent,
  QuickstartPageContent,
} from '@/types/quickstart'
import createContentUrl from '@/utils/createContentUrl'
import type { ButtonVariant } from '@storyblok/design-system'
import type { IconListItem } from '@/components/Auth/Signup/components/IconListItem/types'
import type {
  ReferencedQuoteComponent,
  TestimonialItemComponent,
} from '@/components/Auth/Signup/components/Testimonials/types'
import type { LinkComponent, Logo } from '@/components/Auth/Signup/types'
import type {
  TabInfo,
  FormOption,
  PlanCardData,
} from '@/components/ContentManager/NewSpacePage/types'

export type Cookie = {
  _uid: string
  name: string
  type: string
  domain: string
  storage: string
  duration: string
  component: string
  description: string
  _editable: string
}

export type DemoThemesStoryContent = {
  _uid: string
  themes: DemoTheme[]
  component: 'demo_themes'
}

export type Step = {
  _uid: string
  label: string
  completed: boolean
  component: string
}

export type NewSpaceStoryContent = {
  _uid: string
  tabs: TabInfo[]
  select_plan_header_title: string
  select_plan_header_description: string
  component: string
  enterprise_cards: PlanCardData[]
  form_options: FormOption[]
  plans_cards: PlanCardData[]
  stepper: Step[]
  header_title: string
  header_description: string
  yearly_billing_label: string
  enterprise_headline: ISbRichtext
  enterprise_form_button_label: string
  enterprise_form_button_color: ButtonVariant
  enterprise_form_observation: string
  enterprise_form_title: string
  enterprise_form_description: string
  enterprise_form_footer_text: string
  sales_responsible_image: AssetContentRecord
  sales_responsible_name: string
  sales_responsible_role: string
  poster?: AssetContentRecord
}

export type DemoTheme = {
  _uid: string
  image: AssetContentRecord
  title: string
  caption: string
  spaceId: string
  component: 'theme'
  preview_url: object
}

export type LabsFeatureContent = {
  title: string
  image: AssetContentRecord
  story_slug: string
  not_available_tag_label: string
  available_tag_label: string
  enabled_tag_label: string
  short_description: string
  border_color: string
  background_color: string
  backend_feature_key: string
  backend_app_slug: string
  is_available: boolean
  body: Record<string, unknown>[]
  app_id?: number | undefined
  frontend_feature_route: string
  enable_feature_text: string
  backend_disclaimer_key: string
  notify_user_button_label: string
  enabled_button_label: string
}

export type LabsFeatureStory = {
  _uid: string
  content: LabsFeatureContent
  component: string
  slug: string
}

export type SignUpContent = {
  _uid: string
  component: string
  content: {
    heading: ISbRichtext
    features?: IconListItem[]
    sub_heading?: string
    testimonials?: (TestimonialItemComponent | ReferencedQuoteComponent)[]
    logos?: Logo[]
    footer_link?: LinkComponent[]
  }
}

export type State = {
  cookies: StoryContent
  demos: DemoThemesStoryContent
  newSpaceStoryContent: NewSpaceStoryContent
  hints: DemoSpaceTourContent[]
  labsFeatures: LabsFeatureContent[]
  quickstartContent: QuickstartContent
  quickstartPageContent: QuickstartPageContent
  signupContent: SignUpContent
  tourContent: TourContent[] | undefined
  storiesHintContent: TourContent[] | undefined
}

export const useContent = defineStore('content', {
  state: (): State => ({
    cookies: {} as StoryContent,
    demos: {} as DemoThemesStoryContent,
    newSpaceStoryContent: {} as NewSpaceStoryContent,
    hints: [] as DemoSpaceTourContent[],
    labsFeatures: [] as LabsFeatureContent[],
    quickstartContent: {} as QuickstartContent,
    quickstartPageContent: {} as QuickstartPageContent,
    signupContent: {} as SignUpContent,
    tourContent: [] as TourContent[],
    storiesHintContent: [] as TourContent[],
  }),
  getters: {
    getCookieSettings: (state: State): StoryContent => state.cookies,
    getDemoDetails: (state: State): DemoThemesStoryContent => state.demos,
    getNewSpaceStoryContent: (state: State): NewSpaceStoryContent =>
      state.newSpaceStoryContent,
    getDemoSpaceHints: (state: State): DemoSpaceTourContent[] => state.hints,
    getLabsFeatures: (state: State): LabsFeatureContent[] => state.labsFeatures,
    getQuickstartContent: (state: State): QuickstartContent =>
      state.quickstartContent,
    getQuickstartPageContent: (state: State): QuickstartPageContent =>
      state.quickstartPageContent,
    getSignupContent: (state: State): SignUpContent => state.signupContent,
    getStoriesHintContent: (state: State): TourContent[] | undefined =>
      state.storiesHintContent,
  },
  actions: {
    setCookieSettings(cookies: StoryContent): void {
      this.cookies = cookies
    },
    setDemoDetails(demos: DemoThemesStoryContent): void {
      this.demos = demos
    },
    setDemoSpaceTourHints(hints: DemoSpaceTourContent[]): void {
      this.hints = hints
    },
    setNewSpaceStoryContent(story: NewSpaceStoryContent): void {
      this.newSpaceStoryContent = story
    },
    setQuickstartContent(content: QuickstartContent): void {
      this.quickstartContent = content
    },
    setQuickstartPageContent(content: QuickstartPageContent): void {
      this.quickstartPageContent = content
    },
    setLabsFeatures(features: LabsFeatureContent[]): void {
      this.labsFeatures = features
    },
    setSignupContent(content: SignUpContent): void {
      this.signupContent = content
    },
    setEditorTourContent(content: TourContent[]): void {
      this.tourContent = content
    },
    setStoriesHintContent(content: TourContent[]): void {
      this.storiesHintContent = content
    },
    async loadCookieSettings(): Promise<void> {
      const { data } = await client.get<{ story: { content: StoryContent } }>(
        `https://app.storyblok.com/v1/cdn/stories/general/cookie-settings?${
          createContentUrl().urlParams
        }`,
      )

      return this.setCookieSettings(data.story.content)
    },
    async loadDemoDetails(): Promise<DemoThemesStoryContent> {
      const { data } = await client.get(
        `${DEMO_SPACE_THEME_CONTENT_URL}${createContentUrl().urlParams}`,
      )

      this.setDemoDetails(data.story.content)

      return data.story.content
    },

    async loadNewSpaceStoryContent(): Promise<void> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories/general/create-space-joyride-pricing-model?${
          createContentUrl().urlParams
        }`,
      )

      this.setNewSpaceStoryContent(data.story.content)
    },
    async loadDemoSpaceTourHints(): Promise<void> {
      const { data } = await client.get(
        `${DEMO_SPACE_TOUR_HINTS_CONTENT_URL}${createContentUrl().urlParams}`,
      )

      this.setDemoSpaceTourHints(data?.story?.content?.DemoSpaceTourHint || [])
    },
    async loadLabsFeaturesContent(): Promise<void> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories?starts_with=innovation-labs${
          createContentUrl().urlParams
        }`,
      )

      const cards = data?.stories?.map((story: LabsFeatureStory) => {
        return { ...story.content, story_slug: story.slug }
      })

      this.setLabsFeatures(cards || [])
    },

    async loadLabsFeatureContent(slug: string): Promise<LabsFeatureContent> {
      const { data } = await client.get(
        `https://app.storyblok.com/v1/cdn/stories/innovation-labs/${slug}?${
          createContentUrl().urlParams
        }&resolve_relations=ContentTestimonials.quotes`,
      )

      return {
        ...data.story.content,
        title: data.story.content?.title || data.story.name,
      }
    },

    async loadQuickstartContent(): Promise<void> {
      const { data } = await client.get<{
        story: { content: QuickstartContent }
      }>(
        `https://app.storyblok.com/v1/cdn/stories/onboarding/get-started?${
          createContentUrl().urlParams
        }`,
      )

      return this.setQuickstartContent(data.story.content)
    },

    async loadQuickstartPageContent(jobRole?: JobRoleType): Promise<void> {
      const contentPath = jobRole
        ? `get-started-${jobRole.replace('_', '-')}`
        : 'get-started'

      const { data } = await client.get<{
        story: { content: QuickstartPageContent }
      }>(
        `https://app.storyblok.com/v1/cdn/stories/onboarding/${contentPath}?${
          createContentUrl().urlParams
        }`,
      )

      return this.setQuickstartPageContent(data.story.content)
    },

    async loadSignupContent(): Promise<void> {
      const { data } = await client.get<{ story: SignUpContent }>(
        `https://app.storyblok.com/v1/cdn/stories/onboarding/sign-up-marketing?resolve_relations=app_ui_quote.quote${
          createContentUrl().urlParams
        }`,
      )

      this.setSignupContent(data.story)
    },

    async loadEditorTourContent(): Promise<void> {
      const { data } = await client.get<{
        story: { content: { tour: TourContent[] } }
      }>(
        `https://app.storyblok.com/v1/cdn/stories/onboarding/visual-editor-tour?${
          createContentUrl().urlParams
        }`,
      )

      this.setEditorTourContent(data?.story?.content?.tour)
    },

    async loadStoriesTourContent(): Promise<void> {
      const { data } = await client.get<{
        story: { content: { hint: TourContent[] } }
      }>(
        `https://app.storyblok.com/v1/cdn/stories/onboarding/content-tour?${
          createContentUrl().urlParams
        }`,
      )

      this.setStoriesHintContent(data?.story?.content?.hint)
    },
  },
})
